import { CellContext, ColumnDef } from "@tanstack/react-table";
import { Check, ChevronRight } from "lucide-react";
import { ReactNode } from "react";

import { Badge } from "components/ui/badge";
import { CopyButton } from "components/ui/copy-button";
import { DataTableColumnHeader } from "components/ui/data-table";
import { CaseTrackerRow, CaseTrackerSort } from "ts/interfaces/caseTracker";
import { MoneyFormatCurrencyStyle } from "utils/calcs";
import { Highlight } from "./Highlight";

const sortableColumn = (
  sortKey: CaseTrackerSort,
  accessorKey: keyof CaseTrackerRow,
  title: string,
  cell?: (context: CellContext<CaseTrackerRow, unknown>) => ReactNode,
): ColumnDef<CaseTrackerRow> => ({
  id: sortKey,
  accessorKey,
  // TanStack otherwise picks the first direction from the first row's value,
  // which flips when that row has no value (e.g. no second applicant).
  sortDescFirst: false,
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title={title} />
  ),
  cell:
    cell ?? (({ getValue }) => <Highlight text={getValue<string | null>()} />),
});

const StatusPill = ({
  tone,
  children,
}: {
  tone: "warning" | "success" | "danger";
  children: ReactNode;
}) => (
  <Badge
    variant={tone}
    className="gap-1.5 whitespace-nowrap rounded-full font-medium"
  >
    {tone === "success" && <Check className="h-3 w-3" aria-hidden="true" />}
    {tone === "warning" || tone === "danger" ? (
      <span
        className="h-1.5 w-1.5 rounded-full bg-current"
        aria-hidden="true"
      />
    ) : null}
    {children}
  </Badge>
);

const splitName = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return { first: parts.slice(0, -1).join(" "), last: parts[parts.length - 1] };
};

// "Priya Shah" + "Arjun Shah" -> "Priya & Arjun Shah"
const joinApplicants = (one: string | null, two: string | null) => {
  if (!one || !two) {
    return one || two || "";
  }
  const [a, b] = [splitName(one), splitName(two)];
  if (a.first && b.first && a.last.toLowerCase() === b.last.toLowerCase()) {
    return `${a.first} & ${b.first} ${a.last}`;
  }
  return `${one} & ${two}`;
};

const missingOpenBankingLabel = (row: CaseTrackerRow) => {
  const missing = [
    { linked: row.applicantOneOpenBankingLinked, name: row.applicantOneName },
    { linked: row.applicantTwoOpenBankingLinked, name: row.applicantTwoName },
  ].filter((applicant) => applicant.linked === false);

  if (missing.length === 2) {
    return "Both applicants missing OB";
  }
  if (missing.length === 1) {
    return "1 applicant missing OB (" + missing[0].name + ")";
  }
  return null;
};

const BASE_COLUMNS: ColumnDef<CaseTrackerRow>[] = [
  sortableColumn("REFERENCE", "reference", "Reference", ({ row }) => (
    <div className="whitespace-nowrap">
      <div className="flex items-center gap-1 font-semibold text-foreground">
        <Highlight text={row.original.reference} />
        <CopyButton
          value={row.original.reference}
          label="Copy case reference"
          className="h-6 w-6 [&_svg]:size-3.5"
        />
      </div>
      {row.original.brokerReference && (
        <div className="text-xs text-muted-foreground">
          <Highlight text={row.original.brokerReference} />
        </div>
      )}
    </div>
  )),
  sortableColumn(
    "APPLICANT_ONE_NAME",
    "applicantOneName",
    "Applicants",
    ({ row }) => (
      <div>
        <div className="font-semibold text-foreground">
          <Highlight
            text={joinApplicants(
              row.original.applicantOneName,
              row.original.applicantTwoName,
            )}
          />
        </div>
        <div className="text-xs text-muted-foreground">
          {row.original.applicantTwoName ? "Joint" : "Sole"}
        </div>
      </div>
    ),
  ),
  sortableColumn("POSTCODE", "postcode", "Postcode"),
  {
    ...sortableColumn("LOAN_AMOUNT", "loanAmount", "Loan amount", ({ row }) =>
      row.original.loanAmount === null ? null : (
        <span className="whitespace-nowrap tabular-nums">
          {MoneyFormatCurrencyStyle.format(Number(row.original.loanAmount))}
        </span>
      ),
    ),
    sortDescFirst: true,
  },
];

const openCaseColumn: ColumnDef<CaseTrackerRow> = {
  id: "open",
  header: () => <span className="sr-only">Open case</span>,
  enableSorting: false,
  cell: () => (
    <ChevronRight
      className="ml-auto h-4 w-4 text-muted-foreground"
      aria-hidden="true"
    />
  ),
};

const missingOpenBankingColumn: ColumnDef<CaseTrackerRow> = {
  id: "missingOpenBanking",
  header: "Tasks",
  enableSorting: false,
  cell: ({ row }) => {
    const label = missingOpenBankingLabel(row.original);
    return label ? <StatusPill tone="warning">{label}</StatusPill> : null;
  },
};

// `showNone` adds a "No tasks" pill; otherwise a case without tasks is blank.
const tasksColumn = (showNone: boolean): ColumnDef<CaseTrackerRow> => ({
  ...sortableColumn("OPEN_TASK_COUNT", "openTaskCount", "Tasks", ({ row }) => {
    const count = row.original.openTaskCount;
    if (count > 0) {
      return (
        <StatusPill tone="warning">
          {count} {count === 1 ? "task" : "tasks"}
        </StatusPill>
      );
    }
    return showNone ? <StatusPill tone="success">No tasks</StatusPill> : null;
  }),
  sortDescFirst: true,
});

export const OPEN_BANKING_COLUMNS = [
  ...BASE_COLUMNS,
  missingOpenBankingColumn,
  openCaseColumn,
];

export const UNDERWRITING_COLUMNS = [
  ...BASE_COLUMNS,
  tasksColumn(true),
  openCaseColumn,
];

export const CLOSED_COLUMNS = [...BASE_COLUMNS, openCaseColumn];

export const DEFAULT_COLUMNS = [
  ...BASE_COLUMNS,
  tasksColumn(false),
  openCaseColumn,
];
